import { useEffect, useState } from "react";

import {
  DatePicker,
  TextField,
  Select,
  Checkbox,
  DocumentList,
} from "@king-ict/eupisi-ui/components";
import { CodeBook, RequestStatusEnum } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import {
  getRequest,
  getDifficultyTypeList,
  getRequestDocument,
  getForm6,
  saveForm6,
  getEnrollmentDecision,
} from "../../api/Request/RequestApi";
import { getSchoolYear } from "../../api/SchoolYear/SchoolYearApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  ToolbarTop,
  ToolbarEdit,
  FormCard,
  InstitutionSelect,
  RequestHeader,
} from "../../components";
import { useAuth } from "../../hooks/auth/useAuth";
import { ValidationFailure, Address, Role, SchoolYear } from "../../models";
import {
  Request,
  RequestStatus,
  RequestType,
  DocumentType,
  Form6,
  EnrollmentDecision,
} from "../../models/Request";
import { Attachment } from "../../models/Shared/Attachment";
import { ClaimType } from "../../models/User";
import { requestAdminDetailsUrl } from "../../routes/routes";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";

export function Form6Edit() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const auth = useAuth();

  const createDefault = (): Request => {
    return {
      id: 0,
      requestUID: "",
      code: "",
      statusId: RequestStatus.Draft,
      schoolYearId: 0,
      type: RequestType.RegularEnrollment,
      createdByAdmin: true,
      isOutsideEnrollmentArea: false,
      step: 0,
      subStep: 0,
    };
  };
  const createDefaultForm6 = (): Form6 => {
    return {
      id: 0,
      requestId: +id,
      sessionDate: new Date(),
    };
  };
  const [item, setItem] = useState<Request>(createDefault());
  const [form6, setForm6] = useState<Form6>(createDefaultForm6());
  const [schoolYear, setSchoolYear] = useState<SchoolYear | undefined>();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [errors, setErrors] = useState<ValidationFailure[]>();

  useEffect(() => {
    (async () => {})();
  }, []);
  useEffect(() => {
    (async () => {
      setIsEditing(true); // Assume we don't get one.
      if (id && id !== "0") {
        var result = await getForm6(+id);
        if (result) {
          setForm6(result);
          setIsEditing(false);
          if (result.request) {
            setItem(result.request);
          }
        } else {
          var request = await getRequest(+id);
          if (request) {
            setItem(() => request); // Can we force this to be synchronous?
          }
        }
      }
    })();
    dispatch(resetIsEdited());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => {
    (async () => {
      if (item && item.schoolYearId && item.schoolYearId > 0) {
        var schoolYear = await getSchoolYear(item.schoolYearId);
        setSchoolYear(schoolYear);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.schoolYearId]);
  // useEffect(() => {
  //     if (item.institutionId && form6.id === 0 && item.type === RequestType.EarlyEnrollment) {
  //         // There is a problem with the institutionId not being set on the form7
  //         // although it is shown in the InstitutionSelect. We have not been given
  //         // any default values for the different request types (for EarlyEnrollment,
  //         // is the default Approved or Rejected?). We will set an institution.
  //         // This may be updated based on the selections.
  //         const d: Form6 = {
  //             ...form6,
  //             institutionId: item.institutionId,
  //         };
  //         handleUpdate(d);
  //     }
  // }, [item.institutionId]);

  const handleEditingClick = () => {
    setIsEditing(true);
  };
  const handleSessionDateChanged = (val: Date) => {
    const d: Form6 = {
      ...form6,
      sessionDate: val,
    };
    handleUpdate(d);
  };
  const handleSessionPlaceChanged = (val?: string) => {
    const d: Form6 = {
      ...form6,
      sessionPlace: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleClassificationChange = (val?: string) => {
    const d: Form6 = {
      ...form6,
      classification: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleAssociationNumberChange = (val?: string) => {
    const d: Form6 = {
      ...form6,
      associationNumber: val && val.length > 0 ? val : undefined,
    };
    handleUpdate(d);
  };
  const handleDocumentDownload = async (doc: Attachment) => {
    if (doc && doc.id) {
      await getRequestDocument(doc.id, item.id, DocumentType.Form6);
    }
  };
  const handleUpdate = (item: Form6) => {
    setForm6(item);
    dispatch(setIsEdited());
  };
  //if (auth.currentRole?.claimType !== ClaimType.County) return null;
  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={
          !isEditing &&
          (auth.currentRole?.role === Role.CAS ||
            auth.currentRole?.role === Role.Helpdesk ||
            ((auth.currentRole?.role === Role.CountyLeader ||
              auth.currentRole?.role === Role.CountyAdministrator) &&
              auth.currentRole?.locations?.find((v) => v === item.countyId) !==
                undefined)) &&
          item.statusId !== RequestStatus.Cancelled &&
          item.statusId !== RequestStatus.Registered &&
          (schoolYear?.isCurrent ?? false)
            ? "show"
            : "hide"
        }
        pageTitle={t("form6.pageTitle")}
        onClickEdit={handleEditingClick}
        errors={errors}
      />
      <RequestHeader request={item} />
      <FormCard cardTitle={t("form6.data")}>
        <Row>
          <Col md={12}>
            <DatePicker
              label={t("form6.sessionDate")}
              name={"form6.sessionDate"}
              onChange={(e) =>
                handleSessionDateChanged(new Date(e.target.value))
              }
              value={form6.sessionDate}
              minYear={new Date().getFullYear()}
              plaintext={!isEditing}
              errorText={
                errors?.find((e) => e.propertyName?.startsWith("SessionDate"))
                  ?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <TextField
              label={t("form6.sessionPlace")}
              name={nameof<typeof form6>("sessionPlace")}
              value={form6.sessionPlace}
              onChange={(e) => handleSessionPlaceChanged(e.target.value)}
              plaintext={!isEditing}
              errorText={
                errors?.find((e) => e.propertyName?.startsWith("SessionPlace"))
                  ?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <TextField
              label={t("form6.classification")}
              name={nameof<typeof form6>("classification")}
              value={form6.classification}
              onChange={(e) => handleClassificationChange(e.target.value)}
              maxLength={50}
              plaintext={!isEditing}
              errorText={
                errors?.find((e) =>
                  e.propertyName?.startsWith("Classification")
                )?.message
              }
            />
          </Col>
          <Col md={6}>
            <TextField
              label={t("form6.associationNumber")}
              name={nameof<typeof form6>("associationNumber")}
              value={form6.associationNumber}
              onChange={(e) => handleAssociationNumberChange(e.target.value)}
              maxLength={50}
              plaintext={!isEditing}
              errorText={
                errors?.find((e) =>
                  e.propertyName?.startsWith("AssociationNumber")
                )?.message
              }
            />
          </Col>
        </Row>
        {!isEditing && form6.attachment && (
          <DocumentList
            documents={form6.attachment ? [form6.attachment] : []}
            onDocumentClick={(doc) => handleDocumentDownload(doc)}
            removable={false}
          />
        )}
      </FormCard>
      <ToolbarEdit
        showCancel={"show"}
        showSave={
          !isEditing
            ? "hide"
            : form6 &&
              form6.associationNumber &&
              form6.classification &&
              form6.sessionPlace &&
              form6.sessionDate != new Date()
            ? "show"
            : "disable"
        }
        redirectUrlSaveOK={() => requestAdminDetailsUrl(+id)}
        handleSave={async () => saveForm6(form6)}
        setSaveErrors={setErrors}
        showCustom={false}
      />
    </ContentArea>
  );
}
